import { FormProvider, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";

import { getCustomProviderSteps } from "./constants";

import { DialogContent, DialogTrigger, Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/Stepper";
import { useCreateProvider } from "@/queries/providers/useCreateProvider";
import { usePatchProvider } from "@/queries/providers/usePatchProvider";
import { CustomProvider, ProviderAuthScheme } from "@/queries/providers/types";

interface CustomProviderFormFields {
  name: string;
  client: string;
  base_url: string;
  api_key: string;
  auth_scheme: ProviderAuthScheme;
  extra_headers: string;
}

const EMPTY_FORM: CustomProviderFormFields = {
  name: "",
  client: "",
  base_url: "",
  api_key: "",
  auth_scheme: "bearer",
  extra_headers: "",
};

export const CustomProvidersDialog = ({
  isOpen,
  setIsOpen,
  isEditModalOpen,
  isPreviewModalOpen,
  customProvider,
  setIsEditModalOpen,
  setIsPreviewModalOpen,
  setCustomProvider,
  isLoadingProviders,
  providers,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isEditModalOpen: boolean;
  isPreviewModalOpen: boolean;
  customProvider: CustomProvider | null;
  setIsEditModalOpen: (isEditModalOpen: boolean) => void;
  setIsPreviewModalOpen: (isPreviewModalOpen: boolean) => void;
  setCustomProvider: (customProvider: CustomProvider | null) => void;
  isLoadingProviders: boolean;
  providers: CustomProvider[];
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const methods = useForm<{ create_custom_provider: CustomProviderFormFields }>({
    defaultValues: { create_custom_provider: EMPTY_FORM },
  });
  const { mutate: createProvider, isLoading: isCreatingProvider } = useCreateProvider({
    onSuccess: () => {
      setIsOpen(false);
      methods.reset();
    },
  });
  const { mutate: patchProvider, isLoading: isPatchingProvider } = usePatchProvider({
    onSuccess: () => {
      methods.reset();
      setIsOpen(false);
      setIsEditModalOpen(false);
      setCustomProvider(null);
    },
  });

  useEffect(() => {
    if (isEditModalOpen || isPreviewModalOpen) {
      methods.setValue(
        "create_custom_provider",
        {
          name: customProvider?.name ?? "",
          client: customProvider?.client ?? "",
          base_url: customProvider?.base_url ?? "",
          api_key: "••••••••••••••••••••••••",
          auth_scheme: customProvider?.auth_scheme ?? "bearer",
          extra_headers: customProvider?.extra_headers
            ? JSON.stringify(customProvider.extra_headers, null, 2)
            : "",
        },
        { shouldDirty: true },
      );
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditModalOpen, isPreviewModalOpen]);

  useEffect(() => {
    if (!isOpen) {
      setIsOpen(false);
      setIsEditModalOpen(false);
      setIsPreviewModalOpen(false);
      setCustomProvider(null);
      methods.reset({ create_custom_provider: EMPTY_FORM });
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const buildSubmitPayload = (form: CustomProviderFormFields) => {
    const extraHeaders =
      form.auth_scheme === "custom" && form.extra_headers
        ? (JSON.parse(form.extra_headers) as Record<string, string>)
        : null;
    return {
      name: form.name,
      client: form.client,
      base_url: form.base_url,
      api_key: form.api_key,
      auth_scheme: form.auth_scheme,
      extra_headers: extraHeaders,
    };
  };

  const onSubmit = (data: { create_custom_provider: CustomProviderFormFields }) => {
    const payload = buildSubmitPayload(data.create_custom_provider);
    if (isEditModalOpen) {
      patchProvider({
        ...payload,
        id: customProvider?.id ?? 0,
        api_key: data.create_custom_provider.api_key.includes("••••••••••••••••••••••••")
          ? undefined
          : data.create_custom_provider.api_key,
      });
    } else {
      createProvider(payload);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger disabled={isLoadingProviders} asChild>
        <Button type="button" variant="outline" className="w-fit">
          <Plus className="mr-2 h-4 w-4" />
          Create Custom Provider
        </Button>
      </DialogTrigger>
      <DialogContent className={activeStep > 0 ? "max-w-screen-md" : ""}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stepper
              size="dialog"
              isNextButtonLoading={isCreatingProvider}
              activeStep={activeStep}
              isPreview={isPreviewModalOpen}
              steps={
                isLoadingProviders
                  ? []
                  : getCustomProviderSteps(providers, isPreviewModalOpen, isEditModalOpen)
              }
              isLoading={isPatchingProvider || isCreatingProvider}
              onNext={() => {
                setActiveStep(activeStep + 1);
              }}
              onBack={() => {
                setActiveStep(activeStep - 1);
              }}
              onExit={() => {
                setIsOpen(false);
              }}
            />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
